import LocalStorageService from '@/services/storage/general/local-storage-service';
import Request from '@/services/util/request-service';
import SessionService from '@/services/storage/session-service';
import IndexedDBService from '@/services/storage/general/indexeddb-service';

import { CourseData, CourseId, CourseProgressEntity, SubunitId, UnitId } from 'course-types';

const PROGRESS_STORAGE_KEY = 'U_PROGRESS';

const UserProgressStorageService = {
  getUserCourseProgressCookie(): Record<CourseId, CourseProgressEntity> | undefined {
    return LocalStorageService.get(PROGRESS_STORAGE_KEY) as Record<CourseId, CourseProgressEntity>;
  },

  setUserCourseProgressCookie(data: Record<CourseId, CourseProgressEntity>): void {
    LocalStorageService.set(PROGRESS_STORAGE_KEY, data);
    // Write-through to IndexedDB (fire-and-forget)
    if (IndexedDBService.available()) {
      void IndexedDBService.set(PROGRESS_STORAGE_KEY, data).catch((e) =>
        console.warn('IndexedDB set failed for progress:', e),
      );
    }
    if (typeof window !== 'undefined') {
      try {
        window.dispatchEvent(new CustomEvent('user-progress-updated'));
      } catch (e) {
        void e;
      }
    }
    return;
  },

  resetUserCourseProgressCookie(): void {
    LocalStorageService.delete(PROGRESS_STORAGE_KEY);
    if (IndexedDBService.available()) {
      void IndexedDBService.delete(PROGRESS_STORAGE_KEY).catch((e) =>
        console.warn('IndexedDB delete failed for progress:', e),
      );
    }
    return;
  },

  async retrieveUserCourseProgressCookie(): Promise<void> {
    const progress: Record<CourseId, CourseProgressEntity> | undefined = (
      await (await Request.get(`user/progress`))?.json()
    )?.progress;
    this.setUserCourseProgressCookie(progress ? progress : {});
    return;
  },

  async validateUserCourseProgressCookie(): Promise<void> {
    const cookie = this.getUserCourseProgressCookie();
    if (!cookie) {
      // Try hydrating from IndexedDB first before hitting the network.
      try {
        if (IndexedDBService.available()) {
          const idbProgress =
            await IndexedDBService.get<Record<CourseId, CourseProgressEntity>>(
              PROGRESS_STORAGE_KEY,
            );
          if (idbProgress) {
            this.setUserCourseProgressCookie(idbProgress);
            return;
          }
        }
      } catch (e) {
        console.warn('IndexedDB get failed for progress:', e);
      }

      // Fallback to network if session exists.
      const session = SessionService.getSessionCookie();
      if (session) {
        await this.retrieveUserCourseProgressCookie();
      }
    }
    return;
  },

  async initCourseProgress(courseData: CourseData): Promise<void> {
    // Retrieve course progress
    let changed: boolean = false;
    const userProgress = UserProgressStorageService.getUserCourseProgressCookie();

    // Early cancel if progress cookie undefined (wait until refetch to avoid progress overwrites)
    if (!userProgress) return;

    // Defensive: ensure course structure exists before iterating
    if (
      !courseData ||
      typeof (courseData as any).units !== 'object' ||
      (courseData as any).units === null
    ) {
      return;
    }

    // Initialize course progress if undefined
    if (!userProgress[courseData.id]) {
      changed = true;
      userProgress[courseData.id] = {
        completion_data: { subunits: {}, units: {} },
      };
    }

    // Loop through units
    const unitIDs = Object.keys(courseData.units);
    for (const unitId of unitIDs as Array<UnitId>) {
      // Define unit progress if undefined
      if (!userProgress[courseData.id].completion_data.units[unitId]) {
        changed = true;
        userProgress[courseData.id].completion_data.units[unitId] = {
          quiz: {},
          video: {},
          document: {},
        };
      }

      // Loop through unit activities, add undefined
      for (const activity of courseData.units[unitId].activities) {
        if (activity.type == 'resource') continue;

        if (
          !userProgress[courseData.id].completion_data.units[unitId][activity.type][activity.id]
        ) {
          changed = true;
          userProgress[courseData.id].completion_data.units[unitId][activity.type][activity.id] =
            activity.type == 'quiz' ? { attempts: 0 } : {};
        }
      }

      // Loop through subunits
      const subunitIDs = Object.keys(courseData.units[unitId].subunits);
      for (const subunitId of subunitIDs as Array<SubunitId>) {
        // Define subunit progress if undefined
        if (!userProgress[courseData.id].completion_data.subunits[subunitId]) {
          changed = true;
          userProgress[courseData.id].completion_data.subunits[subunitId] = {
            quiz: {},
            video: {},
            document: {},
          };
        }

        // Loop through subunit activities, add undefined
        for (const subActivity of courseData.units[unitId].subunits[subunitId].activities) {
          if (subActivity.type == 'resource') continue;

          if (
            !userProgress[courseData.id].completion_data.subunits[subunitId][subActivity.type][
              subActivity.id
            ]
          ) {
            changed = true;
            userProgress[courseData.id].completion_data.subunits[subunitId][subActivity.type][
              subActivity.id
            ] = subActivity.type == 'quiz' ? { attempts: 0 } : {};
          }
        }
      }
    }

    // Update course data in cookie and DB
    UserProgressStorageService.setUserCourseProgressCookie(userProgress);

    // Only call 2nd API request if progress structure changed
    if (changed) {
      const res = await (
        await Request.post('user/progress/init', {
          courseId: courseData.id,
          completion_data: userProgress[courseData.id].completion_data,
        })
      )?.json();
    }
  },

  // ---------------------------------------------------------------------------
  // Optional async helpers for direct IndexedDB access (for future migration)
  // ---------------------------------------------------------------------------
  async getUserCourseProgressIndexedDB(): Promise<
    Record<CourseId, CourseProgressEntity> | undefined
  > {
    if (!IndexedDBService.available()) return undefined;
    try {
      return await IndexedDBService.get<Record<CourseId, CourseProgressEntity>>(
        PROGRESS_STORAGE_KEY,
      );
    } catch (e) {
      console.warn('IndexedDB direct get failed for progress:', e);
      return undefined;
    }
  },

  async setUserCourseProgressIndexedDB(
    data: Record<CourseId, CourseProgressEntity>,
  ): Promise<void> {
    if (!IndexedDBService.available()) return;
    try {
      await IndexedDBService.set(PROGRESS_STORAGE_KEY, data);
    } catch (e) {
      console.warn('IndexedDB direct set failed for progress:', e);
    }
  },

  async resetUserCourseProgressIndexedDB(): Promise<void> {
    if (!IndexedDBService.available()) return;
    try {
      await IndexedDBService.delete(PROGRESS_STORAGE_KEY);
    } catch (e) {
      console.warn('IndexedDB direct delete failed for progress:', e);
    }
  },
};

export default UserProgressStorageService;
